/**
 * HTTP route vocabulary for the restaurant → location rename (Phase 3, spec
 * docs/superpowers/specs/2026-09-27-restaurant-to-location-rename-design.md).
 * API routes moved from `/restaurants/...` to `/locations/...`; every old URL stays
 * registered as an alias to the same handler (shipping tills call them). This is the
 * ONE mapping between the two — used by the API to register the alias, by the
 * route-deletion guard to see an alias as a kept route, and by clients to build the
 * new URL. Path params keep their old names in Phase 3 (they are request.params
 * properties; they stay permanently — see the Phase 3 plan's spec gap 6).
 */
import { ValidationError } from '../errors';

/** New literal segment → legacy literal segment. Whole-segment matches only. */
export const LOCATION_ROUTE_SEGMENTS: Readonly<Record<string, string>> = Object.freeze({
  locations: 'restaurants',
  location: 'restaurant',
  'set-location-context': 'set-restaurant-context',
  'me-locations': 'me-restaurants',
  'location-resolve': 'restaurant-resolve',
  'location-preview': 'restaurant-preview',
  'aggregate-ytd-location': 'aggregate-ytd-restaurant',
});
const LEGACY_TO_NEW: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(Object.entries(LOCATION_ROUTE_SEGMENTS).map(([next, legacy]) => [legacy, next])),
);

// OWN keys only: the tables are plain objects, so `table['constructor']` would
// otherwise resolve to Object and `table['toString']` to a function.
const mapSegments = (url: string, table: Readonly<Record<string, string>>): string =>
  url
    .split('/')
    .map((seg) => (seg.startsWith(':') || seg.includes('*') || !Object.hasOwn(table, seg) ? seg : table[seg]))
    .join('/');

export function legacyLocationUrl(url: string): string { return mapSegments(url, LOCATION_ROUTE_SEGMENTS); }
export function newLocationUrl(url: string): string { return mapSegments(url, LEGACY_TO_NEW); }

const badSegment = (v: unknown): boolean =>
  typeof v !== 'string' || v === '' || v.includes('/') || v === 'undefined' || v === 'null';

/** `/tenants/<t>/locations/<l>[/<suffix>]` — the client-side builder for tenant-scoped location URLs. */
export function tenantLocationApiPath(scope: { tenantDocId: string; locationDocId: string }, suffix?: string): string {
  if (badSegment(scope.tenantDocId)) throw new ValidationError(`tenantLocationApiPath: bad tenantDocId ${JSON.stringify(scope.tenantDocId)}`);
  if (badSegment(scope.locationDocId)) throw new ValidationError(`tenantLocationApiPath: bad locationDocId ${JSON.stringify(scope.locationDocId)}`);
  const base = `/tenants/${scope.tenantDocId}/locations/${scope.locationDocId}`;
  if (suffix === undefined) return base;
  if (suffix === '' || suffix.startsWith('/')) throw new ValidationError(`tenantLocationApiPath: bad suffix ${JSON.stringify(suffix)}`);
  return `${base}/${suffix}`;
}
